import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';
import { todayLocalISO } from '../lib/dateUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TransmissionBadge } from '../components/VehicleBadges';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '../components/ui/command';
import { toast } from 'sonner';
import VehicleDocuments from '../components/VehicleDocuments';
import usePasteUpload from '../hooks/usePasteUpload';
import {
  ArrowLeft, Car, Shield, FileCheck, Wrench, AlertTriangle,
  AlertCircle, Upload, Trash2, Loader2, Image as ImageIcon, Download,
  FileText, Pencil, Save, X, LogIn, LogOut, Eye, GripVertical, Building2,
  ZoomIn, ZoomOut, RotateCw, ChevronLeft, ChevronRight, Plus, Circle, Clock, Check, User, Paperclip
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { RentalFields } from '../components/vehicles/RentalFields';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DOC_CATEGORIES = [
  { key: 'keuring', labelKey: 'vehicles.keuring', icon: FileCheck, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { key: 'ruhsat', labelKey: 'vehicles.ruhsat', icon: Car, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
  { key: 'sigorta_policesi', labelKey: 'vehicles.sigortaPolicesi', icon: Shield, color: 'text-violet-600 bg-violet-50 border-violet-200' },
  { key: 'muayene_raporu', labelKey: 'vehicles.muayeneRaporu', icon: FileCheck, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  { key: 'bakim_raporu', labelKey: 'vehicles.bakimRaporu', icon: Wrench, color: 'text-slate-600 bg-slate-50 border-slate-200' },
];

const FUEL_TYPES = ['DIESEL', 'BENZINE', 'EV', 'HYBRID', 'LPG', 'CNG', 'RODE_MAZOUT', 'YOK'];
const FUEL_LABELS = {
  DIESEL: 'Diesel', BENZINE: 'Benzine', EV: 'EV (Elektrik)', HYBRID: 'Hybrid',
  LPG: 'LPG', CNG: 'CNG', RODE_MAZOUT: 'Rode Mazout', YOK: 'Yok'
};
const OWNERSHIP_TYPES = ['Lease', 'Eigendom', 'Kiralık'];
const STICKER_OPTIONS = ['Ja', 'Nee'];
const TIRE_TYPES = [
  { value: 'Y', label: 'Yazlık', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  { value: 'K', label: 'Kışlık', color: 'text-blue-700 bg-blue-50 border-blue-200' },
  { value: 'YK', label: 'Yazlık-Kışlık', color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  { value: 'Yok', label: 'Yok', color: 'text-slate-500 bg-slate-50 border-slate-200' },
];

export default function VehicleDetail() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return ['details', 'documents', 'photos', 'maintenance'].includes(tab) ? tab : 'details';
  });

  // Edit states
  const [editSection, setEditSection] = useState(null); // 'general' | 'technical' | 'ownership' | 'notes' | 'insurance' | 'inspection' | 'maintenance'
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // Documents
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const [dragOver, setDragOver] = useState(null);

  // Photos
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  // Preview modal state (react-pdf for PDFs, zoom/rotate for images)
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [pdfPageNumber, setPdfPageNumber] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(null);

  // Tire history
  const [tireHistory, setTireHistory] = useState([]);
  // Şub 16, 2026 — Sürücü atama geçmişi
  const [assignHistory, setAssignHistory] = useState([]);
  // Şub 16, 2026 — Askıya alma modal state'i
  const [suspendModal, setSuspendModal] = useState({ open: false, reason: '' });
  // Şub 16, 2026 — Durum arşivi & İkame araç geçmişi
  const [statusHistory, setStatusHistory] = useState([]);
  const [firmHistory, setFirmHistory] = useState([]);
  const [substitutions, setSubstitutions] = useState([]);
  const [subModal, setSubModal] = useState({
    open: false, mode: 'create', id: null,
    loaner_plate: '', loaner_brand: '', loaner_model: '', loaner_source: '',
    reason: '', start_date: '', end_date: '', notes: '',
  });
  // Şub 16, 2026 — İkame araç ekleri (in-modal + per-row)
  const [subAttachments, setSubAttachments] = useState({});  // {subId: [{id, file_name, content_type, ...}]}
  const [subUploading, setSubUploading] = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);  // yeni kayıt için modal içinde tutulan dosyalar
  const [dragActive, setDragActive] = useState(false);
  const [tireHistoryLoading, setTireHistoryLoading] = useState(false);
  const [tireDialogOpen, setTireDialogOpen] = useState(false);
  const [tireForm, setTireForm] = useState({ tire_type: '', tire_km: '', tire_date: '', notes: '' });
  const [tireSaving, setTireSaving] = useState(false);

  // Vergunning codes assigned to this vehicle (managed directly here)
  const [vergCodesList, setVergCodesList] = useState([]);          // all codes available (from /vergunning-codes)
  // Şub 2026 — Firma listesi (Genel Bilgiler edit'inde firm seçimi için)
  const [firms, setFirms] = useState([]);
  // Şub 27, 2026 — Marka combobox: aynı marka ikonu havuzu (Vehicles.jsx ile aynı)
  const [brandIcons, setBrandIcons] = useState({});
  const [brandPopoverOpen, setBrandPopoverOpen] = useState(false);
  useEffect(() => {
    api.get('/brand-icons').then(r => {
      const d = r.data?.data || r.data || {};
      const map = Array.isArray(d) ? d.reduce((acc, x) => { acc[String(x.brand).toUpperCase()] = x; return acc; }, {}) : d;
      setBrandIcons(map);
    }).catch(() => {});
  }, []);
  const [vergEditing, setVergEditing] = useState(false);
  const [vergSelected, setVergSelected] = useState([]);            // local draft while editing
  const [vergTypes, setVergTypes] = useState({});                   // Şub 16, 2026 — {codeId: 'ORIGINAL'|'COPY'}
  const [vergSaving, setVergSaving] = useState(false);
  const [vergPickerOpen, setVergPickerOpen] = useState(false);

  const fetchVehicle = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/vehicles/${vehicleId}`);
      if (res.data.success) setVehicle(res.data.data);
    } catch {
      toast.error(t('vehicles.vehicleLoadError'));
      navigate('/dashboard/vehicles');
    } finally {
      setLoading(false);
    }
  }, [vehicleId, navigate]);

  const fetchDocs = useCallback(async () => {
    try {
      setDocsLoading(true);
      const res = await api.get(`/vehicles/${vehicleId}/docs`);
      if (res.data.success) setDocs(res.data.data || []);
    } catch { /* ignore */ } finally { setDocsLoading(false); }
  }, [vehicleId]);

  const fetchPhotos = useCallback(async () => {
    try {
      setPhotosLoading(true);
      const res = await api.get(`/vehicles/${vehicleId}/photos`);
      if (res.data.success) setPhotos(res.data.data || []);
    } catch { /* ignore */ } finally { setPhotosLoading(false); }
  }, [vehicleId]);

  const fetchTireHistory = useCallback(async () => {
    try {
      setTireHistoryLoading(true);
      const res = await api.get(`/vehicles/${vehicleId}/tire-history`);
      if (res.data.success) setTireHistory(res.data.data || []);
    } catch { /* ignore */ } finally { setTireHistoryLoading(false); }
  }, [vehicleId]);

  const fetchVergCodesList = useCallback(async () => {
    try {
      const res = await api.get('/vergunning-codes');
      if (res.data?.success) setVergCodesList(res.data.data || []);
    } catch { /* ignore */ }
  }, []);

  // Şub 2026 — Genel Bilgiler edit'inde firm select için firma listesi
  const fetchFirms = useCallback(async () => {
    try {
      const res = await api.get('/admin/firms');
      if (res.data?.success) setFirms(res.data.data || []);
    } catch { /* ignore */ }
  }, []);

  // Şub 16, 2026 — Sürücü atama geçmişi (newest-first)
  const fetchAssignHistory = useCallback(async () => {
    try {
      const res = await api.get(`/vehicles/${vehicleId}/assignment-history`);
      if (res.data?.success) setAssignHistory(res.data.data || []);
    } catch { /* ignore */ }
  }, [vehicleId]);

  // Şub 16, 2026 — Durum değişikliği arşivi
  const fetchStatusHistory = useCallback(async () => {
    try {
      const res = await api.get(`/vehicles/${vehicleId}/status-history`);
      if (res.data?.success) setStatusHistory(res.data.data || []);
    } catch { /* ignore */ }
  }, [vehicleId]);

  // Şub 2026 — Firma değişim geçmişi (CREATED + FIRM_CHANGED olayları)
  const fetchFirmHistory = useCallback(async () => {
    try {
      const res = await api.get(`/vehicles/${vehicleId}/firm-history`);
      if (res.data?.success) setFirmHistory(res.data.data || []);
    } catch { /* ignore */ }
  }, [vehicleId]);

  // Şub 16, 2026 — İkame araç geçmişi + eklerini paralel çek
  const fetchSubstitutions = useCallback(async () => {
    try {
      const res = await api.get(`/vehicles/${vehicleId}/substitutions`);
      if (res.data?.success) {
        const list = res.data.data || [];
        setSubstitutions(list);
        // Her sub için ekleri paralel çek (max 20 sub için makul)
        const map = {};
        await Promise.all(list.slice(0, 20).map(async (s) => {
          try {
            const r = await api.get(`/vehicle-substitutions/${s.id}/attachments`);
            if (r.data?.success) map[s.id] = r.data.data || [];
          } catch { /* ignore */ }
        }));
        setSubAttachments(map);
      }
    } catch { /* ignore */ }
  }, [vehicleId]);

  useEffect(() => {
    fetchVehicle();
    fetchVergCodesList();
    fetchFirms();
    fetchAssignHistory();
    fetchStatusHistory();
    fetchFirmHistory();
    fetchSubstitutions();
  }, [fetchVehicle, fetchVergCodesList, fetchFirms, fetchAssignHistory, fetchStatusHistory, fetchFirmHistory, fetchSubstitutions]);
  useEffect(() => { if (activeTab === 'documents') fetchDocs(); }, [activeTab, fetchDocs]);
  useEffect(() => { if (activeTab === 'photos') fetchPhotos(); }, [activeTab, fetchPhotos]);
  useEffect(() => { if (activeTab === 'maintenance') fetchTireHistory(); }, [activeTab, fetchTireHistory]);

  // ---- INLINE EDIT ----
  const startEdit = (section, fields) => {
    const data = {};
    fields.forEach(f => { data[f] = vehicle?.[f] || ''; });
    setEditData(data);
    setEditSection(section);
  };

  const cancelEdit = () => { setEditSection(null); setEditData({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { ...editData };
      // Convert empty strings to null for optional fields
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      await api.put(`/vehicles/${vehicleId}`, payload);
      toast.success(t('vehicles.saved'));
      setEditSection(null);
      fetchVehicle();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('vehicles.saveError'));
    } finally { setSaving(false); }
  };

  // ---- DOCUMENT HANDLERS ----
  const handleDocUpload = async (files, category, extraFields = {}) => {
    if (!files?.length) return;
    setUploading(prev => ({ ...prev, [category]: true }));
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', category);
        if (extraFields.vergunning_number) {
          fd.append('vergunning_number', String(extraFields.vergunning_number));
        }
        await api.post(`/vehicles/${vehicleId}/docs`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(t('vehicles.docUploaded', { count: files.length }));
      fetchDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('vehicles.uploadError'));
    } finally {
      setUploading(prev => ({ ...prev, [category]: false }));
    }
  };

  const handleDocDelete = async (docId) => {
    try {
      await api.delete(`/vehicles/${vehicleId}/docs/${docId}`);
      toast.success(t('vehicles.docDeleted'));
      fetchDocs();
    } catch { toast.error(t('vehicles.deleteError')); }
  };

  const handleVergCodesSave = async () => {
    setVergSaving(true);
    try {
      await api.put(`/vehicles/${vehicleId}/vergunning-codes`, { codes: vergSelected, types: vergTypes });
      toast.success('Vergunning kodları güncellendi');
      setVergEditing(false);
      fetchVehicle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncelleme hatası');
    } finally { setVergSaving(false); }
  };

  const openVergCodeFile = async (codeId, fileName) => {
    // Find the code meta from our list to enrich preview modal.
    const codeDoc = vergCodesList.find(c => c.id === codeId);
    const isPdf = (fileName || '').toLowerCase().endsWith('.pdf') || codeDoc?.file_content_type === 'application/pdf';
    const isImage = (codeDoc?.file_content_type || '').startsWith('image/');
    const contentType = codeDoc?.file_content_type || (isPdf ? 'application/pdf' : 'image/*');

    setPreviewPhoto({ id: codeId, file_name: fileName, content_type: contentType, _isVergCode: true });
    setPreviewLoading(true);
    setPreviewError(null);
    setZoom(1);
    setRotation(0);
    setPdfPageNumber(1);
    setPdfNumPages(null);
    setPreviewUrl(null);

    try {
      const response = await api.get(`/vergunning-codes/${codeId}/file`, { responseType: 'blob' });
      const blob = response.data;
      setPreviewUrl(URL.createObjectURL(blob));
      if (!isPdf && !isImage) {
        // Direct download if the file isn't previewable.
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = fileName || 'vergunning';
        a.click();
        closePreview();
      }
    } catch {
      setPreviewError(t('vehicles.previewError') || 'Önizleme hatası');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUpdateVergNumber = async (docId, number) => {
    try {
      await api.put(`/vehicles/${vehicleId}/docs/${docId}`, { vergunning_number: number ? parseInt(number) : null });
      toast.success('Vergunning numarası güncellendi');
      fetchDocs();
    } catch { toast.error('Güncelleme hatası'); }
  };

  const handleToggleInVehicle = async (docId, currentValue) => {
    try {
      await api.put(`/vehicles/${vehicleId}/docs/${docId}`, { in_vehicle: !currentValue });
      fetchDocs();
    } catch { toast.error(t('vehicles.updateError')); }
  };

  // ---- PHOTO HANDLERS ----
  const handlePhotoUpload = async (files) => {
    if (!files?.length) return;
    setPhotoUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/vehicles/${vehicleId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(t('vehicles.photoUploaded', { count: files.length }));
      fetchPhotos();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('vehicles.uploadError'));
    } finally { setPhotoUploading(false); }
  };

  const handlePhotoDelete = async (photoId) => {
    try {
      await api.delete(`/vehicles/${vehicleId}/photos/${photoId}`);
      toast.success(t('vehicles.photoDeleted'));
      fetchPhotos();
    } catch { toast.error(t('vehicles.deleteError')); }
  };

  // ---- TIRE HISTORY HANDLERS ----
  const handleAddTireHistory = async () => {
    if (!tireForm.tire_type) {
      toast.error('Lastik tipi seçiniz');
      return;
    }
    setTireSaving(true);
    try {
      const payload = {
        tire_type: tireForm.tire_type,
        tire_km: tireForm.tire_km ? parseInt(tireForm.tire_km, 10) : null,
        tire_date: tireForm.tire_date || null,
        notes: tireForm.notes || '',
      };
      await api.post(`/vehicles/${vehicleId}/tire-history`, payload);
      toast.success('Lastik değişimi kaydedildi');
      setTireDialogOpen(false);
      setTireForm({ tire_type: '', tire_km: '', tire_date: '', notes: '' });
      fetchTireHistory();
      fetchVehicle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kayıt sırasında hata oluştu');
    } finally { setTireSaving(false); }
  };

  const handleDeleteTireHistory = async (entryId) => {
    try {
      await api.delete(`/vehicles/${vehicleId}/tire-history/${entryId}`);
      toast.success('Lastik geçmişi kaydı silindi');
      fetchTireHistory();
      fetchVehicle();
    } catch { toast.error('Silme sırasında hata oluştu'); }
  };

  // Drag handlers
  const onDragOver = (e, key) => { e.preventDefault(); setDragOver(key); };
  const onDragLeave = () => setDragOver(null);
  const onDrop = (e, category) => { e.preventDefault(); setDragOver(null); handleDocUpload(Array.from(e.dataTransfer.files), category); };
  const onPhotoDragOver = (e) => { e.preventDefault(); setPhotoDragOver(true); };
  const onPhotoDragLeave = () => setPhotoDragOver(false);
  const onPhotoDrop = (e) => { e.preventDefault(); setPhotoDragOver(false); handlePhotoUpload(Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'))); };

  // ─── Ctrl+V (Paste) → Aktif sekmeye göre yükleme ───────────────────────────
  // - Photos sekmesinde: pano resmi/PDF → handlePhotoUpload (mevcut drag&drop
  //   ile birebir aynı kod yolu).
  // - Documents sekmesinde: VehicleDocuments component'i kendi paste hook'unu
  //   yönetir; bu nedenle burada documents için ayrı kayıt yapmıyoruz.
  usePasteUpload(
    (files) => handlePhotoUpload(files.filter((f) => f.type.startsWith('image/') || f.type === 'application/pdf')),
    { enabled: activeTab === 'photos' && !!vehicleId, accept: 'image/*,application/pdf' }
  );

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Preview helpers
  const isImageContent = (item) => item?.content_type?.startsWith('image/');
  const isPdfContent = (item) => item?.content_type === 'application/pdf' || item?.file_name?.toLowerCase().endsWith('.pdf');

  const openPreview = async (item, isDoc = false) => {
    const isPdf = isPdfContent(item);
    const isImage = isImageContent(item);
    if (!isPdf && !isImage) return;

    setPreviewPhoto({ ...item, _isDoc: isDoc });
    setPreviewLoading(true);
    setPreviewError(null);
    setZoom(1);
    setRotation(0);
    setPdfPageNumber(1);
    setPdfNumPages(null);
    setPreviewUrl(null);

    try {
      // Şub 27, 2026 — CORS-safe: sadece image için direct CDN URL kullan;
      // PDF/DOC için CORS ayarları yapılana kadar eski blob akışına düş.
      const presignPath = isDoc
        ? `/vehicles/${vehicleId}/docs/${item.id}/download-url?preview=true`
        : `/vehicles/${vehicleId}/photos/${item.id}/download-url`;
      try {
        const pres = await api.get(presignPath);
        const directUrl = pres.data?.data?.url;
        const ct = pres.data?.data?.content_type || '';
        const corsSafe = !isDoc || ct.toLowerCase().startsWith('image/');
        if (directUrl && corsSafe) {
          setPreviewUrl(directUrl);
          setPreviewLoading(false);
          return;
        }
      } catch (_) {
        /* eski akışa düş */
      }

      const url = isDoc
        ? `${API_URL}/api/vehicles/${vehicleId}/docs/${item.id}/download`
        : `${API_URL}/api/vehicles/${vehicleId}/photos/${item.id}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewUrl(blobUrl);
    } catch {
      setPreviewError(t('vehicles.previewError'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    // Blob URL'yi revoke et; direct CDN URL revoke edilemez ama sorun değil.
    if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setPreviewPhoto(null);
    setPreviewUrl(null);
    setPreviewError(null);
  };

  const onPdfLoadSuccess = ({ numPages }) => setPdfNumPages(numPages);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;
  if (!vehicle) return null;

  const isSold = vehicle.is_sold;

  return (
    <div className="space-y-6" data-testid="vehicle-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Car className="h-6 w-6 text-blue-600" />
              {vehicle.license_plate}
            </h1>
            {vehicle.brand && <Badge variant="outline" className="text-sm">{vehicle.brand} {vehicle.model}</Badge>}
            {isSold && <Badge variant="destructive" className="text-xs">{t('vehicles.sold')} - {new Date(vehicle.sale_date).toLocaleDateString('tr-TR')}</Badge>}
            {vehicle.status === 'SUSPENDED' && (
              <Badge variant="destructive" className="text-xs" data-testid="vehicle-suspended-badge">
                Askıya Alındı
              </Badge>
            )}
            {vehicle.status === 'MAINTENANCE' && (
              <Badge className="text-xs bg-violet-600 text-white" data-testid="vehicle-maintenance-badge">Bakımda</Badge>
            )}
            {vehicle.ownership_type === 'Kiralık' && (
              <Badge variant="outline" className="text-xs border-sky-300 text-sky-700 bg-sky-50" data-testid="vehicle-rental-badge">
                Kiralık{vehicle.rental_end_date ? ` · dönüş ${new Date(vehicle.rental_end_date).toLocaleDateString('tr-TR')}` : ''}
              </Badge>
            )}
            {vehicle.firm_name && <Badge variant="secondary" className="text-xs"><Building2 className="h-3 w-3 mr-1" />{vehicle.firm_name}</Badge>}
          </div>
          {vehicle.warnings?.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {vehicle.warnings.map((w, i) => (
                <Badge key={i} variant="outline" className={w.level === 'critical' ? 'border-red-300 text-red-600 bg-red-50' : 'border-amber-300 text-amber-600 bg-amber-50'}>
                  {w.level === 'critical' ? <AlertCircle className="h-3 w-3 mr-1" /> : <AlertTriangle className="h-3 w-3 mr-1" />}
                  {w.message}
                </Badge>
              ))}
            </div>
          )}
        </div>
        {/* Şub 16, 2026 — Aracı askıya al / aktif et (sebep modal ile) */}
        {(!vehicle.status || vehicle.status === 'ACTIVE') ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSuspendModal({ open: true, reason: '', target: 'MAINTENANCE' })}
              className="text-violet-700 border-violet-200 hover:bg-violet-50" data-testid="vehicle-maintenance-btn">Bakıma Al</Button>
            <Button variant="outline" size="sm" onClick={() => setSuspendModal({ open: true, reason: '', target: 'SUSPENDED' })}
              className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="vehicle-suspend-btn">Askıya Al</Button>
          </div>
        ) : (
          <Button size="sm" onClick={() => setSuspendModal({ open: true, reason: '', target: 'ACTIVE' })}
            className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="vehicle-suspend-btn">Aktif Et</Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="details" data-testid="tab-details"><Car className="h-4 w-4 mr-2" />{t('vehicles.vehicleDetailsSection')}</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><FileText className="h-4 w-4 mr-2" />{t('vehicles.documents')}</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos"><ImageIcon className="h-4 w-4 mr-2" />{t('vehicles.photos')}</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance"><Wrench className="h-4 w-4 mr-2" />{t('vehicles.maintenanceInsurance')}</TabsTrigger>
        </TabsList>

        {/* ====== DETAILS TAB ====== */}
        <TabsContent value="details" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Genel Bilgiler */}
            <EditableCard
              title={t('vehicles.generalInfo')}
              isEditing={editSection === 'general'}
              onEdit={() => startEdit('general', ['license_plate', 'brand', 'model', 'year', 'color', 'firm_id', 'transmission'])}
              onSave={saveEdit}
              onCancel={cancelEdit}
              saving={saving}
              t={t}
            >
              {editSection === 'general' ? (
                <div className="space-y-3">
                  <EditField label={t('vehicles.plate')} value={editData.license_plate} onChange={v => setEditData({...editData, license_plate: v})} />
                  {/* Şub 27, 2026 — Marka Combobox: kayıtlı markalar + arama + icon */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">{t('vehicles.brand')}</label>
                    <Popover open={brandPopoverOpen} onOpenChange={setBrandPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          className="w-full h-9 justify-between font-normal"
                          data-testid="vd-brand-combobox"
                        >
                          <span className="flex items-center gap-2 truncate">
                            {editData.brand && brandIcons[editData.brand.trim().toUpperCase()] && (
                              <img
                                src={`${API_URL}/api/brand-icons/${encodeURIComponent(editData.brand.trim().toUpperCase())}/image`}
                                alt=""
                                className="h-5 w-5 object-contain"
                              />
                            )}
                            <span className={editData.brand ? '' : 'text-slate-400'}>
                              {editData.brand || (t('vehicles.brandPlaceholder') || 'Marka seç...')}
                            </span>
                          </span>
                          <ChevronRight className="h-4 w-4 opacity-50 rotate-90" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder={t('vehicles.searchBrand') || 'Marka ara...'} className="h-9" />
                          <CommandList className="max-h-72">
                            <CommandEmpty>{t('vehicles.noBrand') || 'Marka bulunamadı.'}</CommandEmpty>
                            <CommandGroup heading={t('vehicles.registeredBrands') || 'Kayıtlı Markalar'}>
                              {Object.keys(brandIcons).sort().map((b) => (
                                <CommandItem
                                  key={b}
                                  value={b}
                                  onSelect={() => { setEditData({ ...editData, brand: b }); setBrandPopoverOpen(false); }}
                                  className="cursor-pointer"
                                >
                                  <img
                                    src={`${API_URL}/api/brand-icons/${encodeURIComponent(b)}/image`}
                                    alt=""
                                    className="h-5 w-5 object-contain mr-2"
                                  />
                                  <span>{b}</span>
                                  {editData.brand?.trim().toUpperCase() === b && (
                                    <Check className="ml-auto h-4 w-4 text-teal-600" />
                                  )}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <EditField label={t('vehicles.model')} value={editData.model} onChange={v => setEditData({...editData, model: v})} />
                  <EditField label={t('vehicles.buildYear')} value={editData.year} onChange={v => setEditData({...editData, year: v})} type="number" />
                  <EditField label={t('vehicles.color')} value={editData.color} onChange={v => setEditData({...editData, color: v})} />
                  {/* Şub 2026 — Firma değişikliği artık ayrıntı sayfasından yapılabilir; her değişiklik Firma Geçmişi'ne loglanır */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">{t('vehicles.firm')}</label>
                    <select
                      className="w-full h-9 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm"
                      value={editData.firm_id || ''}
                      onChange={(e) => setEditData({ ...editData, firm_id: e.target.value })}
                      data-testid="vd-firm-select"
                    >
                      <option value="">— Seçiniz —</option>
                      {firms.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </div>
                  {/* Şub 2026 — Vites tipi */}
                  <EditSelect
                    label="Vites"
                    value={editData.transmission || ''}
                    onChange={v => setEditData({ ...editData, transmission: v })}
                    options={['AUTOMATIC', 'MANUAL']}
                    labels={{ AUTOMATIC: 'Otomatik', MANUAL: 'Manuel' }}
                    t={t}
                  />
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <InfoRow label={t('vehicles.plate')} value={vehicle.license_plate} mono />
                  <InfoRow label={t('vehicles.brand')} value={vehicle.brand} />
                  <InfoRow label={t('vehicles.model')} value={vehicle.model} />
                  <InfoRow label={t('vehicles.buildYear')} value={vehicle.year} />
                  <InfoRow label={t('vehicles.color')} value={vehicle.color} />
                  <InfoRow label={t('vehicles.firm')} value={vehicle.firm_name} />
                  <InfoRow
                    label="Vites"
                    value={vehicle.transmission ? (
                      <span className="inline-flex items-center gap-2">
                        <TransmissionBadge value={vehicle.transmission} size={18} />
                        {vehicle.transmission === 'AUTOMATIC' ? 'Otomatik' : 'Manuel'}
                      </span>
                    ) : '—'}
                  />
                </div>
              )}
            </EditableCard>

            {/* Teknik Bilgiler */}
            <EditableCard
              title={t('vehicles.technicalInfo')}
              isEditing={editSection === 'technical'}
              onEdit={() => startEdit('technical', ['chassis_number', 'load_capacity', 'fuel_type', 'permit_number', 'sticker'])}
              onSave={saveEdit}
              onCancel={cancelEdit}
              saving={saving}
              t={t}
            >
              {editSection === 'technical' ? (
                <div className="space-y-3">
                  <EditField label={t('vehicles.chassisNumber')} value={editData.chassis_number} onChange={v => setEditData({...editData, chassis_number: v})} />
                  <EditField label={t('vehicles.loadCapacity')} value={editData.load_capacity} onChange={v => setEditData({...editData, load_capacity: v})} />
                  <EditSelect label={t('vehicles.fuelType')} value={editData.fuel_type} onChange={v => setEditData({...editData, fuel_type: v})} options={FUEL_TYPES} labels={FUEL_LABELS} t={t} />
                  <EditField label={t('vehicles.permitNumber')} value={editData.permit_number} onChange={v => setEditData({...editData, permit_number: v})} />
                  <EditSelect label={t('vehicles.sticker')} value={editData.sticker} onChange={v => setEditData({...editData, sticker: v})} options={STICKER_OPTIONS} t={t} />
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <InfoRow label={t('vehicles.chassisNumber')} value={vehicle.chassis_number} mono />
                  <InfoRow label={t('vehicles.loadCapacity')} value={vehicle.load_capacity} />
                  <InfoRow label={t('vehicles.fuelType')} value={vehicle.fuel_type} badge />
                  <InfoRow label={t('vehicles.permitNumber')} value={vehicle.permit_number} />
                  <InfoRow label={t('vehicles.sticker')} value={vehicle.sticker} />
                </div>
              )}
            </EditableCard>

            {/* Sahiplik & Satis */}
            <EditableCard
              title={t('vehicles.ownershipSale')}
              isEditing={editSection === 'ownership'}
              onEdit={() => startEdit('ownership', ['ownership_type', 'insurance_company', 'purchase_location', 'purchase_date', 'sale_date', 'rental_company', 'rental_start_date', 'rental_end_date', 'rental_monthly_fee', 'rental_daily_fee', 'replaces_vehicle_id'])}
              onSave={saveEdit}
              onCancel={cancelEdit}
              saving={saving}
              t={t}
            >
              {editSection === 'ownership' ? (
                <div className="space-y-3">
                  <EditSelect label={t('vehicles.ownershipType')} value={editData.ownership_type} onChange={v => setEditData({...editData, ownership_type: v})} options={OWNERSHIP_TYPES} t={t} />
                  <EditField label={t('vehicles.insuranceCompany')} value={editData.insurance_company} onChange={v => setEditData({...editData, insurance_company: v})} />
                  <EditField label={t('vehicles.purchaseLocation')} value={editData.purchase_location} onChange={v => setEditData({...editData, purchase_location: v})} />
                  <EditField label={t('vehicles.purchaseDate')} value={editData.purchase_date} onChange={v => setEditData({...editData, purchase_date: v})} type="date" />
                  <EditField label={t('vehicles.saleDate')} value={editData.sale_date} onChange={v => setEditData({...editData, sale_date: v})} type="date" />
                  {editData.ownership_type === 'Kiralık' && <RentalFields formData={editData} setFormData={setEditData} excludeId={vehicleId} />}
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <InfoRow label={t('vehicles.ownershipType')} value={vehicle.ownership_type} />
                  {vehicle.ownership_type === 'Kiralık' && (
                    <div className="rounded-lg border border-sky-200 bg-sky-50/60 dark:bg-sky-950/20 dark:border-sky-900 p-2 space-y-2" data-testid="vehicle-rental-info">
                      <InfoRow label="Yerine geldiği araç" value={vehicle.replaces_plate} />
                      <InfoRow label="Kiralama firması" value={vehicle.rental_company} />
                      <InfoRow label="Kiralama tarihi" value={vehicle.rental_start_date ? new Date(vehicle.rental_start_date).toLocaleDateString('tr-TR') : null} />
                      <InfoRow label="Dönüş tarihi" value={vehicle.rental_end_date ? new Date(vehicle.rental_end_date).toLocaleDateString('tr-TR') : null} highlight />
                      <InfoRow label="Aylık ücret" value={vehicle.rental_monthly_fee != null ? `€${vehicle.rental_monthly_fee}` : null} />
                      <InfoRow label="Günlük ücret" value={vehicle.rental_daily_fee != null ? `€${vehicle.rental_daily_fee}` : null} />
                    </div>
                  )}
                  <InfoRow label={t('vehicles.insuranceCompany')} value={vehicle.insurance_company} />
                  <InfoRow label={t('vehicles.purchaseLocation')} value={vehicle.purchase_location} />
                  <InfoRow label={t('vehicles.purchaseDate')} value={vehicle.purchase_date ? new Date(vehicle.purchase_date).toLocaleDateString('tr-TR') : null} />
                  <InfoRow label={t('vehicles.saleDate')} value={vehicle.sale_date ? new Date(vehicle.sale_date).toLocaleDateString('tr-TR') : null} highlight={!!vehicle.sale_date} />
                  {/* Şub 16, 2026 — Şu anki sürücü — vehicle_assignments'tan */}
                  {(() => {
                    const current = assignHistory.find(a => !a.unassigned_at);
                    return (
                      <InfoRow
                        label="Şu anki Sürücü"
                        value={current ? `${current.user_full_name} (${current.assigned_at ? new Date(current.assigned_at).toLocaleDateString('tr-TR') : '—'} tarihinden itibaren)` : 'Atanmamış'}
                        highlight={!!current}
                      />
                    );
                  })()}
                </div>
              )}
            </EditableCard>

            {/* Vergunning Kodları */}
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileCheck className="h-5 w-5 text-teal-500" />
                  Vergunning
                  {vehicle.vergunning_codes?.length > 0 && (
                    <Badge variant="secondary" className="text-xs bg-teal-50 text-teal-700 border border-teal-200">
                      {vehicle.vergunning_codes.length}
                    </Badge>
                  )}
                </CardTitle>
                {!vergEditing ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      setVergSelected(vehicle.vergunning_codes || []);
                      setVergTypes(vehicle.vergunning_types || {});
                      setVergEditing(true);
                    }}
                    data-testid="verg-edit-btn"
                  >
                    <Pencil className="h-3 w-3 mr-1" />
                    {t('common.edit') || 'Düzenle'}
                  </Button>
                ) : (
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setVergEditing(false); setVergPickerOpen(false); }}>
                      <X className="h-3 w-3 mr-1" />{t('common.cancel') || 'İptal'}
                    </Button>
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-teal-600 hover:bg-teal-700"
                      onClick={handleVergCodesSave}
                      disabled={vergSaving}
                      data-testid="verg-save-btn"
                    >
                      {vergSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                      {t('common.save') || 'Kaydet'}
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                {!vergEditing ? (
                  vehicle.vergunning_codes?.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5" data-testid="verg-assigned-chips">
                      {vehicle.vergunning_codes.map(code => {
                        const meta = vergCodesList.find(c => c.code === code && (!vehicle.firm_id || c.firm_id === vehicle.firm_id)) || vergCodesList.find(c => c.code === code);
                        const hasFile = !!meta?.file_name;
                        return (
                          <button
                            key={code}
                            type="button"
                            onClick={() => hasFile && openVergCodeFile(meta.id, meta.file_name)}
                            disabled={!hasFile}
                            title={hasFile ? `${meta.file_name} — tıkla, önizle` : 'Bu kod için belge yüklenmemiş'}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md font-mono text-sm border transition-colors ${
                              hasFile
                                ? 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100 hover:border-teal-400 cursor-pointer'
                                : 'bg-slate-50 border-slate-200 text-slate-500 cursor-not-allowed'
                            }`}
                            data-testid={`verg-chip-${code}`}
                          >
                            <span>{meta?.code || code}</span>
                            {/* Şub 16, 2026 — Orijinal/Kopya rozeti */}
                            {(() => {
                              const t = (vehicle.vergunning_types || {})[code];
                              if (t === 'ORIGINAL') return <span className="text-[9px] px-1 rounded bg-emerald-600 text-white font-bold" data-testid={`verg-type-badge-${code}`}>O</span>;
                              if (t === 'COPY') return <span className="text-[9px] px-1 rounded bg-rose-600 text-white font-bold" data-testid={`verg-type-badge-${code}`}>K</span>;
                              return null;
                            })()}
                            {hasFile ? (
                              <FileText className="h-3.5 w-3.5 text-teal-600" />
                            ) : (
                              <FileText className="h-3.5 w-3.5 opacity-30" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">Atanmış kod yok</p>
                  )
                ) : (
                  <div className="space-y-2">
                    {/* Selected codes preview */}
                    {vergSelected.length > 0 && (
                      <div className="flex flex-wrap gap-1 p-2 bg-slate-50 dark:bg-slate-800 rounded-md">
                        {vergSelected.map(code => (
                          <span key={code} className="inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded bg-teal-100 text-teal-700">
                            {code}
                            <button onClick={() => setVergSelected(prev => prev.filter(c => c !== code))}>
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Multi-select popover */}
                    <Popover open={vergPickerOpen} onOpenChange={setVergPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-start h-9 text-sm" data-testid="verg-picker-trigger">
                          <Plus className="h-3.5 w-3.5 mr-2 text-teal-600" />
                          Kod ekle/kaldır ({vergSelected.length} seçili)
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[320px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Kod veya firma ara..." className="h-9" />
                          <CommandList className="max-h-64">
                            <CommandEmpty>Sonuç bulunamadı.</CommandEmpty>
                            {[...new Set(vergCodesList.map(c => c.firm_id))].map(firmId => {
                              // Şub 16, 2026 — Bir araca birden fazla firmanın vergunning'i eklenebilsin.
                              // Firm filtresi kaldırıldı; tüm firmaların kodları grupla listelenir.
                              const firmCodes = vergCodesList.filter(c => c.firm_id === firmId);
                              if (firmCodes.length === 0) return null;
                              const firmName = firmCodes[0]?.firm_name || '';
                              return (
                                <CommandGroup key={firmId} heading={firmName}>
                                  {firmCodes.map(vc => {
                                    const active = vergSelected.includes(vc.code);
                                    const t = vergTypes[vc.code] || 'ORIGINAL';
                                    return (
                                      <CommandItem
                                        key={vc.id}
                                        value={`${vc.code} ${firmName}`}
                                        onSelect={() => {
                                          setVergSelected(prev => active ? prev.filter(c => c !== vc.code) : [...prev, vc.code]);
                                          if (!active && !vergTypes[vc.code]) {
                                            setVergTypes(prev => ({ ...prev, [vc.code]: 'ORIGINAL' }));
                                          }
                                        }}
                                        className="cursor-pointer"
                                      >
                                        <div className={`flex h-4 w-4 items-center justify-center rounded border ${active ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300'}`}>
                                          {active && <Check className="h-3 w-3" />}
                                        </div>
                                        <span className="font-mono text-sm ml-2">{vc.code}</span>
                                        {active && (
                                          <span
                                            className="ml-2 inline-flex rounded overflow-hidden text-[10px] font-bold"
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <button
                                              type="button"
                                              onPointerDown={(e) => e.stopPropagation()}
                                              onMouseDown={(e) => e.stopPropagation()}
                                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setVergTypes(prev => ({ ...prev, [vc.code]: 'ORIGINAL' })); }}
                                              className={`px-1.5 py-0.5 ${t === 'ORIGINAL' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'}`}
                                              data-testid={`verg-toggle-original-${vc.code}`}
                                            >Orijinal</button>
                                            <button
                                              type="button"
                                              onPointerDown={(e) => e.stopPropagation()}
                                              onMouseDown={(e) => e.stopPropagation()}
                                              onClick={(e) => { e.stopPropagation(); e.preventDefault(); setVergTypes(prev => ({ ...prev, [vc.code]: 'COPY' })); }}
                                              className={`px-1.5 py-0.5 ${t === 'COPY' ? 'bg-rose-600 text-white' : 'bg-slate-100 text-slate-500'}`}
                                              data-testid={`verg-toggle-copy-${vc.code}`}
                                            >Kopya</button>
                                          </span>
                                        )}
                                        {vc.file_name && <FileText className="h-3 w-3 ml-auto text-teal-500" title="Belge yüklü" />}
                                      </CommandItem>
                                    );
                                  })}
                                </CommandGroup>
                              );
                            })}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <p className="text-xs text-slate-500">
                      Belge varsa görüntüleme kodun kendisinden yapılır — araç ayrıntıları belgeler sekmesinden vergunning yüklemeye gerek yok.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Şub 16, 2026 — Sürücü Atama Geçmişi */}
            <Card data-testid="vehicle-assignment-history-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-500" />
                  Sürücü Geçmişi
                  {assignHistory.length > 0 && (
                    <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700 border border-blue-200">
                      {assignHistory.length}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {assignHistory.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">Henüz atama yok</p>
                ) : (
                  <div className="space-y-2">
                    {assignHistory.map((a) => {
                      const isActive = !a.unassigned_at;
                      const start = a.assigned_at ? new Date(a.assigned_at).toLocaleDateString('tr-TR') : '—';
                      const end = a.unassigned_at ? new Date(a.unassigned_at).toLocaleDateString('tr-TR') : 'devam ediyor';
                      return (
                        <div
                          key={a.id}
                          className={`flex items-center justify-between rounded-md border px-3 py-2 text-sm ${
                            isActive ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30' : 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700'
                          }`}
                          data-testid={`vehicle-assign-row-${a.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {isActive && <Badge className="bg-emerald-600 text-white text-[10px] h-5">Aktif</Badge>}
                            <span className="font-medium truncate">{a.user_full_name || '—'}</span>
                          </div>
                          <div className="text-xs text-slate-500 whitespace-nowrap ml-2 font-mono">
                            {start} → {end}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Şub 16, 2026 — İkame Araçlar (Loaner Substitutions) */}
            <Card data-testid="vehicle-substitutions-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Car className="h-5 w-5 text-purple-500" />
                  İkame Araç Geçmişi
                  {substitutions.length > 0 && (
                    <Badge variant="secondary" className="text-xs bg-purple-50 text-purple-700 border border-purple-200">
                      {substitutions.length}
                    </Badge>
                  )}
                  <Button
                    size="sm" variant="outline"
                    className="ml-auto h-7 text-xs text-purple-700 border-purple-200 hover:bg-purple-50"
                    onClick={() => setSubModal({
                      open: true, mode: 'create', id: null,
                      loaner_plate: '', loaner_brand: '', loaner_model: '', loaner_source: '',
                      reason: '', start_date: new Date().toISOString().slice(0, 10), end_date: '', notes: '',
                    })}
                    data-testid="vehicle-add-substitution-btn"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> İkame Ekle
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {substitutions.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">Henüz ikame araç kaydı yok</p>
                ) : (
                  <div className="space-y-2">
                    {substitutions.map((s) => {
                      const isActive = !s.end_date;
                      return (
                        <div
                          key={s.id}
                          className={`rounded-md border px-3 py-2 text-sm ${isActive ? 'bg-purple-50 border-purple-200 dark:bg-purple-950/30' : 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700'}`}
                          data-testid={`vehicle-sub-row-${s.id}`}
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0">
                              {isActive && <Badge className="bg-purple-600 text-white text-[10px] h-5">Devam Ediyor</Badge>}
                              <span className="font-mono font-semibold">{s.loaner_plate}</span>
                              {(s.loaner_brand || s.loaner_model) && (
                                <span className="text-xs text-slate-500">{s.loaner_brand} {s.loaner_model}</span>
                              )}
                              {s.loaner_source && (
                                <Badge variant="outline" className="text-[10px]">{s.loaner_source}</Badge>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 whitespace-nowrap font-mono">
                              {s.start_date || '—'} → {s.end_date || 'devam ediyor'}
                            </div>
                          </div>
                          {s.reason && (
                            <div className="text-xs text-slate-500 mt-1">Sebep: {s.reason}</div>
                          )}
                          {s.notes && (
                            <div className="text-xs text-slate-400 mt-0.5 italic">{s.notes}</div>
                          )}
                          {/* Şub 16, 2026 — Bu ikame için yüklü ekler */}
                          {(subAttachments[s.id] || []).length > 0 && (
                            <div className="grid grid-cols-6 sm:grid-cols-8 gap-1 mt-2" data-testid={`vehicle-sub-attachments-${s.id}`}>
                              {(subAttachments[s.id] || []).map((att) => (
                                <SubAttachThumb key={att.id} att={att} onDelete={async () => {
                                  if (!window.confirm('Bu ek silinsin mi?')) return;
                                  try {
                                    await api.delete(`/vehicle-substitution-attachments/${att.id}`);
                                    fetchSubstitutions();
                                  } catch (err) { toast.error(err?.response?.data?.detail || 'Silinemedi'); }
                                }} />
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-1.5">
                            {/* Ek yükleme butonu — her satır için ayrı input */}
                            <label className="text-[11px] text-purple-700 hover:underline cursor-pointer" data-testid={`vehicle-sub-add-att-${s.id}`}>
                              {subUploading === s.id ? 'Yükleniyor…' : '+ Ek Yükle'}
                              <input type="file" multiple accept="image/*,.pdf" className="hidden"
                                onChange={async (e) => {
                                  const files = Array.from(e.target.files || []);
                                  if (!files.length) return;
                                  setSubUploading(s.id);
                                  try {
                                    const fd = new FormData();
                                    files.forEach((f) => fd.append('files', f, f.name));
                                    await api.post(`/vehicle-substitutions/${s.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                    toast.success(`${files.length} dosya eklendi`);
                                    fetchSubstitutions();
                                  } catch (err) { toast.error(err?.response?.data?.detail || 'Yüklenemedi'); }
                                  finally { setSubUploading(null); e.target.value = ''; }
                                }} />
                            </label>
                            {isActive && (
                              <button
                                type="button"
                                onClick={async () => {
                                  const ed = window.prompt('Dönüş tarihi (YYYY-MM-DD):', new Date().toISOString().slice(0, 10));
                                  if (!ed) return;
                                  try {
                                    await api.put(`/vehicle-substitutions/${s.id}`, { end_date: ed });
                                    toast.success('İkame araç dönüş tarihi kaydedildi');
                                    fetchSubstitutions();
                                  } catch (err) {
                                    toast.error(err?.response?.data?.detail || 'Güncellenemedi');
                                  }
                                }}
                                className="text-[11px] text-emerald-700 hover:underline"
                                data-testid={`vehicle-sub-end-${s.id}`}
                              >
                                Dönüşü Kaydet
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={async () => {
                                if (!window.confirm('Bu ikame araç kaydı silinsin mi?')) return;
                                try {
                                  await api.delete(`/vehicle-substitutions/${s.id}`);
                                  toast.success('Silindi');
                                  fetchSubstitutions();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Silinemedi');
                                }
                              }}
                              className="text-[11px] text-rose-600 hover:underline"
                              data-testid={`vehicle-sub-delete-${s.id}`}
                            >
                              Sil
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Şub 2026 — Firma Değişim Geçmişi (CREATED + FIRM_CHANGED) */}
            {firmHistory.length > 0 && (
              <Card data-testid="vehicle-firm-history-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-indigo-500" />
                    Firma Geçmişi
                    <Badge variant="secondary" className="text-xs bg-indigo-50 text-indigo-700 border border-indigo-200">{firmHistory.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {firmHistory.map((h) => (
                      <div key={h.id} className="rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm bg-white dark:bg-slate-900" data-testid={`vehicle-firm-row-${h.id}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 text-xs">
                            {h.event_type === 'CREATED' ? (
                              <>
                                <Badge className="bg-teal-600 text-white">Oluşturuldu</Badge>
                                {h.to_firm_name && <span className="font-medium">{h.to_firm_name}</span>}
                              </>
                            ) : (
                              <>
                                <span className="font-medium">{h.from_firm_name || '—'}</span>
                                <span>→</span>
                                <Badge className="bg-indigo-600 text-white">{h.to_firm_name || '—'}</Badge>
                              </>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono">
                            {h.changed_at ? new Date(h.changed_at).toLocaleString('tr-TR') : '—'}
                          </div>
                        </div>
                        {h.changed_by_user_name && (
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {h.event_type === 'CREATED' ? 'Ekleyen: ' : 'Değiştiren: '}{h.changed_by_user_name}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Şub 16, 2026 — Askıya Alma/Aktif Etme Arşivi */}
            {statusHistory.length > 0 && (
              <Card data-testid="vehicle-status-history-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-500" />
                    Durum Geçmişi
                    <Badge variant="secondary" className="text-xs bg-amber-50 text-amber-700 border border-amber-200">{statusHistory.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {statusHistory.map((h) => (
                      <div key={h.id} className="rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm bg-white dark:bg-slate-900" data-testid={`vehicle-status-row-${h.id}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-mono">{h.from_status || 'ACTIVE'}</span>
                            <span>→</span>
                            <Badge className={h.to_status === 'SUSPENDED' ? 'bg-rose-600 text-white' : 'bg-emerald-600 text-white'}>{h.to_status}</Badge>
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono">
                            {h.changed_at ? new Date(h.changed_at).toLocaleString('tr-TR') : '—'}
                          </div>
                        </div>
                        {h.reason && <div className="text-xs mt-1">Sebep: {h.reason}</div>}
                        {h.changed_by_user_name && <div className="text-[11px] text-slate-400 mt-0.5">{h.changed_by_user_name}</div>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <EditableCard
              title={t('vehicles.notes')}
              isEditing={editSection === 'notes'}
              onEdit={() => startEdit('notes', ['notes'])}
              onSave={saveEdit}
              onCancel={cancelEdit}
              saving={saving}
              t={t}
            >
              {editSection === 'notes' ? (
                <textarea
                  className="w-full min-h-[100px] px-3 py-2 border rounded-md text-sm bg-white dark:bg-slate-900 dark:border-slate-700"
                  value={editData.notes || ''}
                  onChange={e => setEditData({...editData, notes: e.target.value})}
                />
              ) : (
                <p className="text-sm text-slate-600 dark:text-slate-300">{vehicle.notes || t('vehicles.noNotes')}</p>
              )}
            </EditableCard>
          </div>
        </TabsContent>

        {/* ====== DOCUMENTS TAB ====== */}
        <TabsContent value="documents" className="space-y-4 mt-4">
          <VehicleDocuments
            vehicleId={vehicleId}
            vehiclePlate={vehicle?.license_plate || vehicle?.plate_number || ''}
          />
        </TabsContent>

        {/* ====== PHOTOS TAB ====== */}
        <TabsContent value="photos" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-violet-500" />
                {t('vehicles.photos') || 'Photos'}
                <Badge variant="secondary" className="text-xs">{photos.length}</Badge>
              </CardTitle>
              <label>
                <input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={(e) => { handlePhotoUpload(Array.from(e.target.files)); e.target.value = ''; }} />
                <Button variant="outline" size="sm" asChild disabled={photoUploading}>
                  <span>{photoUploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}{t('vehicles.uploadPhoto') || 'Upload'}</span>
                </Button>
              </label>
            </CardHeader>
            <CardContent>
              {photosLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
              ) : (
                <div
                  className={`transition-all ${photoDragOver ? 'ring-2 ring-violet-400 bg-violet-50/50 rounded-lg' : ''}`}
                  onDragOver={onPhotoDragOver} onDragLeave={onPhotoDragLeave} onDrop={onPhotoDrop}
                >
                  {photos.length === 0 ? (
                    <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg py-16 text-center text-slate-400">
                      <ImageIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">{t('vehicles.dragDropPhotos') || 'Drag and drop photos here'}</p>
                      <p className="text-xs mt-1">{t('vehicles.orUseButton') || 'or use the upload button'} · <span className="font-medium text-blue-600 dark:text-blue-400">Ctrl+V</span> ile yapıştır</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                      {photos.map(photo => {
                        const isPdf = photo.content_type === 'application/pdf' || photo.file_name?.toLowerCase().endsWith('.pdf');
                        const photoUrl = `${API_URL}/api/vehicles/${vehicleId}/photos/${photo.id}`;
                        return (
                          <div key={photo.id} className="group relative aspect-square rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 cursor-pointer hover:ring-2 hover:ring-blue-400 transition-all" onClick={() => openPreview(photo, false)}>
                            {isPdf ? (
                              <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 dark:bg-red-900/20">
                                <FileText className="h-10 w-10 text-red-500 mb-2" />
                                <span className="text-xs text-slate-600 dark:text-slate-400 px-2 text-center truncate max-w-full">{photo.file_name}</span>
                              </div>
                            ) : (
                              <img src={photoUrl} alt={photo.file_name} className="w-full h-full object-cover" loading="lazy" />
                            )}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2">
                              <a href={photoUrl} download={photo.file_name} onClick={(e) => e.stopPropagation()} className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-1.5 hover:bg-white" title={t('common.download') || 'Download'}>
                                <Download className="h-4 w-4 text-slate-700" />
                              </a>
                              <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-1.5">
                                <Eye className="h-4 w-4 text-slate-700" />
                              </span>
                            </div>
                            <Button variant="destructive" size="icon" className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); handlePhotoDelete(photo.id); }}>
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ====== MAINTENANCE TAB ====== */}
        <TabsContent value="maintenance" className="space-y-4 mt-4">
          {vehicle.warnings?.length > 0 && (
            <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-5 w-5" />{t('vehicles.activeWarnings')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {vehicle.warnings.map((w, i) => (
                    <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${w.level === 'critical' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                      {w.level === 'critical' ? <AlertCircle className="h-5 w-5 text-red-500 shrink-0" /> : <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />}
                      <p className={`text-sm font-medium ${w.level === 'critical' ? 'text-red-700' : 'text-amber-700'}`}>{w.message}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Insurance */}
            <EditableCard
              title={t('vehicles.insurance')}
              icon={<Shield className="h-5 w-5 text-violet-500" />}
              isEditing={editSection === 'insurance'}
              onEdit={() => startEdit('insurance', ['insurance_company', 'insurance_expiry_date'])}
              onSave={saveEdit}
              onCancel={cancelEdit}
              saving={saving}
              highlight={vehicle.warnings?.some(w => w.type === 'insurance')}
              t={t}
            >
              {editSection === 'insurance' ? (
                <div className="space-y-3">
                  <EditField label={t('vehicles.insuranceCompany')} value={editData.insurance_company} onChange={v => setEditData({...editData, insurance_company: v})} />
                  <EditField label={t('vehicles.expiryDate')} value={editData.insurance_expiry_date} onChange={v => setEditData({...editData, insurance_expiry_date: v})} type="date" />
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <InfoRow label={t('vehicles.insuranceCompany')} value={vehicle.insurance_company} />
                  <InfoRow label={t('vehicles.expiryDate')} value={vehicle.insurance_expiry_date ? new Date(vehicle.insurance_expiry_date).toLocaleDateString('tr-TR') : null} highlight={vehicle.warnings?.some(w => w.type === 'insurance' && w.level === 'critical')} warn={vehicle.warnings?.some(w => w.type === 'insurance' && w.level === 'warning')} />
                </div>
              )}
            </EditableCard>

            {/* Inspection */}
            <EditableCard
              title={t('vehicles.inspection')}
              icon={<FileCheck className="h-5 w-5 text-blue-500" />}
              isEditing={editSection === 'inspection'}
              onEdit={() => startEdit('inspection', ['inspection_expiry_date'])}
              onSave={saveEdit}
              onCancel={cancelEdit}
              saving={saving}
              highlight={vehicle.warnings?.some(w => w.type === 'inspection')}
              t={t}
            >
              {editSection === 'inspection' ? (
                <div className="space-y-3">
                  <EditField label={t('vehicles.expiryDate')} value={editData.inspection_expiry_date} onChange={v => setEditData({...editData, inspection_expiry_date: v})} type="date" />
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <InfoRow label={t('vehicles.expiryDate')} value={vehicle.inspection_expiry_date ? new Date(vehicle.inspection_expiry_date).toLocaleDateString('tr-TR') : null} highlight={vehicle.warnings?.some(w => w.type === 'inspection' && w.level === 'critical')} warn={vehicle.warnings?.some(w => w.type === 'inspection' && w.level === 'warning')} />
                </div>
              )}
            </EditableCard>

            {/* Maintenance */}
            <EditableCard
              title={t('vehicles.maintenance')}
              icon={<Wrench className="h-5 w-5 text-emerald-500" />}
              isEditing={editSection === 'maintenance'}
              onEdit={() => startEdit('maintenance', ['last_maintenance_date', 'next_maintenance_date', 'maintenance_km', 'next_maintenance_km'])}
              onSave={saveEdit}
              onCancel={cancelEdit}
              saving={saving}
              highlight={vehicle.warnings?.some(w => w.type === 'maintenance')}
              t={t}
            >
              {editSection === 'maintenance' ? (
                <div className="space-y-3">
                  <EditField label={t('vehicles.lastMaintenance')} value={editData.last_maintenance_date} onChange={v => setEditData({...editData, last_maintenance_date: v})} type="date" />
                  <EditField label={t('vehicles.nextMaintenance')} value={editData.next_maintenance_date} onChange={v => setEditData({...editData, next_maintenance_date: v})} type="date" />
                  <EditField label={t('vehicles.lastMaintenanceKm')} value={editData.maintenance_km} onChange={v => setEditData({...editData, maintenance_km: v})} type="number" />
                  <EditField label={t('vehicles.nextMaintenanceKm')} value={editData.next_maintenance_km} onChange={v => setEditData({...editData, next_maintenance_km: v})} type="number" />
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <InfoRow label={t('vehicles.lastMaintenance')} value={vehicle.last_maintenance_date ? new Date(vehicle.last_maintenance_date).toLocaleDateString('tr-TR') : null} />
                  <InfoRow label={t('vehicles.nextMaintenance')} value={vehicle.next_maintenance_date ? new Date(vehicle.next_maintenance_date).toLocaleDateString('tr-TR') : null} highlight={vehicle.warnings?.some(w => w.type === 'maintenance' && w.level === 'critical')} warn={vehicle.warnings?.some(w => w.type === 'maintenance' && w.level === 'warning')} />
                  <InfoRow label={t('vehicles.lastMaintenanceKm')} value={vehicle.maintenance_km ? `${vehicle.maintenance_km.toLocaleString()} km` : null} />
                  <InfoRow label={t('vehicles.nextMaintenanceKm')} value={vehicle.next_maintenance_km ? `${vehicle.next_maintenance_km.toLocaleString()} km` : null} />
                </div>
              )}
            </EditableCard>
          </div>

          {/* ====== TIRE HISTORY SECTION ====== */}
          <Card data-testid="tire-history-card">
            <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base flex items-center gap-2">
                <Circle className="h-5 w-5 text-teal-500" />
                Lastik Değişim Geçmişi
                <Badge variant="secondary" className="text-xs ml-1">{tireHistory.length}</Badge>
              </CardTitle>
              <Button
                size="sm"
                className="h-8 text-xs gap-1"
                onClick={() => {
                  setTireForm({ tire_type: '', tire_km: '', tire_date: todayLocalISO(), notes: '' });
                  setTireDialogOpen(true);
                }}
                data-testid="add-tire-history-btn"
              >
                <Plus className="h-3.5 w-3.5" />
                Lastik Değişimi Ekle
              </Button>
            </CardHeader>
            <CardContent>
              {tireHistoryLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
              ) : tireHistory.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">
                  <Circle className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p>Henüz lastik değişim kaydı bulunmuyor</p>
                </div>
              ) : (
                <div className="relative pl-6">
                  {/* Timeline line */}
                  <div className="absolute left-[9px] top-2 bottom-2 w-px bg-slate-200 dark:bg-slate-700" />

                  <div className="space-y-4">
                    {tireHistory.map((entry, idx) => {
                      const tireInfo = TIRE_TYPES.find(t => t.value === entry.tire_type) || TIRE_TYPES[3];
                      return (
                        <div key={entry.id} className="relative group" data-testid={`tire-history-entry-${idx}`}>
                          {/* Timeline dot */}
                          <div className={`absolute -left-6 top-3 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center ${idx === 0 ? 'bg-teal-500 border-teal-500' : 'bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600'}`}>
                            {idx === 0 && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </div>

                          <div className={`ml-2 p-3 rounded-lg border transition-colors ${idx === 0 ? 'bg-teal-50/50 border-teal-200 dark:bg-teal-900/10 dark:border-teal-800' : 'bg-slate-50 border-slate-100 dark:bg-slate-800/50 dark:border-slate-700'}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className={`text-xs ${tireInfo.color}`}>
                                  {tireInfo.label}
                                </Badge>
                                {entry.tire_km != null && (
                                  <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
                                    {entry.tire_km.toLocaleString('tr-TR')} km
                                  </span>
                                )}
                                <span className="flex items-center gap-1 text-xs text-slate-400">
                                  <Clock className="h-3 w-3" />
                                  {entry.tire_date ? new Date(entry.tire_date).toLocaleDateString('tr-TR') : '-'}
                                </span>
                                {idx === 0 && (
                                  <Badge className="text-[10px] h-4 bg-teal-500/10 text-teal-700 border-teal-200">
                                    Güncel
                                  </Badge>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                onClick={() => handleDeleteTireHistory(entry.id)}
                                data-testid={`delete-tire-history-${idx}`}
                              >
                                <Trash2 className="h-3 w-3 text-red-500" />
                              </Button>
                            </div>
                            {entry.notes && (
                              <p className="text-xs text-slate-500 mt-1.5">{entry.notes}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ====== TIRE HISTORY DIALOG ====== */}
      <Dialog open={tireDialogOpen} onOpenChange={setTireDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="tire-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Circle className="h-5 w-5 text-teal-500" />
              Lastik Değişimi Ekle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Lastik Tipi *</Label>
              <Select value={tireForm.tire_type} onValueChange={v => setTireForm({...tireForm, tire_type: v})}>
                <SelectTrigger className="h-9" data-testid="tire-type-select">
                  <SelectValue placeholder="Seçiniz..." />
                </SelectTrigger>
                <SelectContent>
                  {TIRE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label} ({t.value})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">KM</Label>
              <Input
                type="number"
                placeholder="Örn: 85000"
                value={tireForm.tire_km}
                onChange={e => setTireForm({...tireForm, tire_km: e.target.value})}
                className="h-9"
                data-testid="tire-km-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Tarih</Label>
              <Input
                type="date"
                value={tireForm.tire_date}
                onChange={e => setTireForm({...tireForm, tire_date: e.target.value})}
                className="h-9"
                data-testid="tire-date-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Not</Label>
              <Input
                placeholder="Opsiyonel not..."
                value={tireForm.notes}
                onChange={e => setTireForm({...tireForm, notes: e.target.value})}
                className="h-9"
                data-testid="tire-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTireDialogOpen(false)} className="text-xs h-8">
              İptal
            </Button>
            <Button onClick={handleAddTireHistory} disabled={tireSaving} className="text-xs h-8 gap-1" data-testid="save-tire-history-btn">
              {tireSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== PREVIEW DIALOG (PDF + IMAGE) - Outside Tabs for global access ====== */}
      {previewPhoto && (
        <Dialog open={!!previewPhoto} onOpenChange={closePreview}>
          <DialogContent className="max-w-5xl max-h-[90vh] p-0 overflow-hidden">
            <DialogHeader className="flex flex-row items-center justify-between px-4 py-3 border-b bg-slate-50 dark:bg-slate-900 gap-2">
              <DialogTitle className="text-sm font-medium truncate flex-1">{previewPhoto.file_name}</DialogTitle>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom(z => Math.max(0.25, z - 0.25))} data-testid="preview-zoom-out">
                  <ZoomOut className="h-3.5 w-3.5" />
                </Button>
                <span className="text-xs text-slate-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setZoom(z => Math.min(3, z + 0.25))} data-testid="preview-zoom-in">
                  <ZoomIn className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setRotation(r => (r + 90) % 360)} data-testid="preview-rotate">
                  <RotateCw className="h-3.5 w-3.5" />
                </Button>
                <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
                {previewPhoto._isVergCode || previewPhoto._isDoc ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs shrink-0"
                    data-testid="preview-download"
                    onClick={() => {
                      if (!previewUrl) return;
                      const a = document.createElement('a');
                      a.href = previewUrl;
                      a.download = (
                        previewPhoto.file_name
                        || previewPhoto.original_filename
                        || previewPhoto.doc_name
                        || 'belge'
                      );
                      a.click();
                    }}
                  >
                    <Download className="h-3 w-3 mr-1" />{t('common.download')}
                  </Button>
                ) : (
                  <a
                    href={`${API_URL}/api/vehicles/${vehicleId}/photos/${previewPhoto.id}`}
                    download={previewPhoto.file_name || 'foto'}
                    className="shrink-0"
                  >
                    <Button variant="outline" size="sm" className="h-7 text-xs" data-testid="preview-download">
                      <Download className="h-3 w-3 mr-1" />{t('common.download')}
                    </Button>
                  </a>
                )}
              </div>
            </DialogHeader>
            <div className="overflow-auto flex items-center justify-center bg-slate-100 dark:bg-slate-950" style={{ maxHeight: 'calc(90vh - 60px)', minHeight: '300px' }}>
              {previewLoading && (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-3" />
                  <p className="text-sm text-slate-500">{t('vehicles.loadingPreview')}</p>
                </div>
              )}
              {previewError && (
                <div className="flex flex-col items-center justify-center py-20">
                  <AlertCircle className="h-8 w-8 text-red-400 mb-3" />
                  <p className="text-sm text-red-500">{previewError}</p>
                </div>
              )}
              {previewUrl && isPdfContent(previewPhoto) && (
                <div className="flex flex-col items-center py-4">
                  <Document
                    file={previewUrl}
                    onLoadSuccess={onPdfLoadSuccess}
                    onLoadError={async (err) => {
                      // Şub 27, 2026 — Direct CDN URL CORS engeline takılırsa
                      // otomatik olarak backend'den blob çekmeye düş.
                      if (previewUrl && !previewUrl.startsWith('blob:')) {
                        try {
                          const url = previewPhoto._isDoc
                            ? `${API_URL}/api/vehicles/${vehicleId}/docs/${previewPhoto.id}/download`
                            : `${API_URL}/api/vehicles/${vehicleId}/photos/${previewPhoto.id}`;
                          const response = await fetch(url);
                          if (response.ok) {
                            const blob = await response.blob();
                            setPreviewUrl(URL.createObjectURL(blob));
                            return;
                          }
                        } catch (_) { /* aşağıdaki hata state'ine düş */ }
                      }
                      console.error('PDF load error:', err);
                    }}
                    loading={<Loader2 className="h-6 w-6 animate-spin text-blue-500" />}
                    error={<p className="text-sm text-red-500">{t('vehicles.pdfLoadError')}</p>}
                  >
                    <Page
                      pageNumber={pdfPageNumber}
                      scale={zoom}
                      rotate={rotation}
                      renderTextLayer
                      renderAnnotationLayer
                    />
                  </Document>
                  {pdfNumPages && pdfNumPages > 1 && (
                    <div className="flex items-center gap-3 mt-3 pb-2">
                      <Button variant="ghost" size="icon" className="h-7 w-7" disabled={pdfPageNumber <= 1} onClick={() => setPdfPageNumber(p => p - 1)}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-xs text-slate-500">{pdfPageNumber} / {pdfNumPages}</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" disabled={pdfPageNumber >= pdfNumPages} onClick={() => setPdfPageNumber(p => p + 1)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}
              {previewUrl && isImageContent(previewPhoto) && (
                <img
                  src={previewUrl}
                  alt={previewPhoto.file_name}
                  className="transition-transform duration-200"
                  style={{ transform: `scale(${zoom}) rotate(${rotation}deg)`, maxWidth: '100%' }}
                  onError={async () => {
                    // Direct URL yüklenemezse (nadir) blob'a fallback
                    if (previewUrl && !previewUrl.startsWith('blob:')) {
                      try {
                        const url = previewPhoto._isDoc
                          ? `${API_URL}/api/vehicles/${vehicleId}/docs/${previewPhoto.id}/download`
                          : `${API_URL}/api/vehicles/${vehicleId}/photos/${previewPhoto.id}`;
                        const response = await fetch(url);
                        if (response.ok) {
                          const blob = await response.blob();
                          setPreviewUrl(URL.createObjectURL(blob));
                        }
                      } catch (_) { /* sessiz */ }
                    }
                  }}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Şub 16, 2026 — Askıya Alma / Aktif Etme Modal (sebep zorunlu) */}
      {suspendModal.open && (() => {
        const target = suspendModal.target || (vehicle.status === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED');
        const willSuspend = target !== 'ACTIVE';
        const isMaint = target === 'MAINTENANCE';
        return (
          <Dialog open={suspendModal.open} onOpenChange={(o) => !o && setSuspendModal({ open: false, reason: '' })}>
            <DialogContent className="max-w-md" data-testid="vehicle-suspend-modal">
              <DialogHeader>
                <DialogTitle>{isMaint ? 'Aracı Bakıma Al' : willSuspend ? 'Aracı Askıya Al' : 'Aracı Aktif Et'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  {isMaint ? 'Bakımdaki araçlar şoföre atanamaz; mevcut şoför ataması kaldırılır. Bakım bitince Aktif Et ile geri alın.' : willSuspend
                    ? 'Askıdaki araçlar planlamaya çıkmaz; mevcut şoför ataması kaldırılır. Sebep arşive kaydedilir.'
                    : 'Araç yeniden aktif edilecek. Değişiklik nedeni arşive yazılır.'}
                </p>
                <label className="flex flex-col gap-1">
                  <span className="text-xs text-slate-500">Sebep *</span>
                  <textarea
                    value={suspendModal.reason}
                    onChange={(e) => setSuspendModal((s) => ({ ...s, reason: e.target.value }))}
                    className="border rounded px-2 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700"
                    rows={3}
                    placeholder={willSuspend ? 'ör. Bakıma gitti, servisi bekliyor, hasar…' : 'ör. Serviste tamamlandı, aktif kullanımda…'}
                    data-testid="vehicle-suspend-reason"
                    autoFocus
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => setSuspendModal({ open: false, reason: '' })}>İptal</Button>
                <Button
                  size="sm"
                  disabled={!suspendModal.reason.trim()}
                  className={willSuspend ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                  onClick={async () => {
                    try {
                      await api.put(`/vehicles/${vehicleId}`, {
                        status: target,
                        status_reason: suspendModal.reason.trim(),
                      });
                      toast.success(isMaint ? 'Araç bakıma alındı' : willSuspend ? 'Araç askıya alındı' : 'Araç aktif edildi');
                      setSuspendModal({ open: false, reason: '' });
                      fetchVehicle();
                      fetchStatusHistory();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || 'İşlem başarısız');
                    }
                  }}
                  data-testid="vehicle-suspend-confirm"
                >
                  {isMaint ? 'Bakıma Al' : willSuspend ? 'Askıya Al' : 'Aktif Et'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Şub 16, 2026 — İkame Araç Ekle Modal */}
      {subModal.open && (
        <Dialog open={subModal.open} onOpenChange={(o) => !o && setSubModal((s) => ({ ...s, open: false }))}>
          <DialogContent className="max-w-lg" data-testid="vehicle-sub-modal">
            <DialogHeader>
              <DialogTitle>İkame Araç Ekle</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 col-span-2">
                <span className="text-xs text-slate-500">İkame Plaka *</span>
                <input
                  type="text" value={subModal.loaner_plate}
                  onChange={(e) => setSubModal((s) => ({ ...s, loaner_plate: e.target.value.toUpperCase() }))}
                  className="border rounded px-2 py-1.5 font-mono uppercase dark:bg-slate-800 dark:border-slate-700"
                  data-testid="vehicle-sub-plate" placeholder="1-XYZ-999"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Marka</span>
                <input type="text" value={subModal.loaner_brand}
                  onChange={(e) => setSubModal((s) => ({ ...s, loaner_brand: e.target.value }))}
                  className="border rounded px-2 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700"
                  data-testid="vehicle-sub-brand" placeholder="Renault" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Model</span>
                <input type="text" value={subModal.loaner_model}
                  onChange={(e) => setSubModal((s) => ({ ...s, loaner_model: e.target.value }))}
                  className="border rounded px-2 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700"
                  data-testid="vehicle-sub-model" placeholder="Master" />
              </label>
              <label className="flex flex-col gap-1 col-span-2">
                <span className="text-xs text-slate-500">Kaynak</span>
                <select
                  value={subModal.loaner_source}
                  onChange={(e) => setSubModal((s) => ({ ...s, loaner_source: e.target.value }))}
                  className="border rounded px-2 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700"
                  data-testid="vehicle-sub-source"
                >
                  <option value="">— seçin —</option>
                  <option value="kiralık">Kiralık</option>
                  <option value="kendi filomuz">Kendi Filomuz</option>
                  <option value="başka firma">Başka Firma</option>
                  <option value="servis loaner">Servis Loaner</option>
                  <option value="diğer">Diğer</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Başlangıç *</span>
                <input type="date" value={subModal.start_date}
                  onChange={(e) => setSubModal((s) => ({ ...s, start_date: e.target.value }))}
                  className="border rounded px-2 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700"
                  data-testid="vehicle-sub-start" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-slate-500">Dönüş (opsiyonel)</span>
                <input type="date" value={subModal.end_date}
                  onChange={(e) => setSubModal((s) => ({ ...s, end_date: e.target.value }))}
                  className="border rounded px-2 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700"
                  data-testid="vehicle-sub-end-input" />
              </label>
              <label className="flex flex-col gap-1 col-span-2">
                <span className="text-xs text-slate-500">Sebep</span>
                <input type="text" value={subModal.reason}
                  onChange={(e) => setSubModal((s) => ({ ...s, reason: e.target.value }))}
                  className="border rounded px-2 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700"
                  data-testid="vehicle-sub-reason" placeholder="Bakım / Kaza / Tamir…" />
              </label>
              <label className="flex flex-col gap-1 col-span-2">
                <span className="text-xs text-slate-500">Notlar</span>
                <textarea value={subModal.notes}
                  onChange={(e) => setSubModal((s) => ({ ...s, notes: e.target.value }))}
                  className="border rounded px-2 py-1.5 text-sm dark:bg-slate-800 dark:border-slate-700" rows={2}
                  data-testid="vehicle-sub-notes" />
              </label>

              {/* Şub 16, 2026 — Ekler: drag&drop + file picker */}
              <div className="col-span-2 flex flex-col gap-2">
                <span className="text-xs text-slate-500 flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> Ekler (opsiyonel, max 20 · 20MB/dosya)</span>
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={(e) => {
                    e.preventDefault(); setDragActive(false);
                    const files = Array.from(e.dataTransfer?.files || []);
                    if (files.length) setPendingAttachments((p) => [...p, ...files].slice(0, 20));
                  }}
                  onClick={() => document.getElementById('sub-file-input')?.click()}
                  className={`rounded border-2 border-dashed p-3 text-center text-xs cursor-pointer transition ${
                    dragActive ? 'border-purple-500 bg-purple-50' : 'border-slate-300 hover:border-purple-400 hover:bg-slate-50'
                  }`}
                  data-testid="vehicle-sub-dropzone"
                >
                  <Upload className={`h-5 w-5 mx-auto mb-1 ${dragActive ? 'text-purple-600' : 'text-slate-400'}`} />
                  <span className="hidden sm:inline">Sürükle bırak veya </span>
                  <span className="text-purple-600 underline">dosya seçin</span>
                  <div className="text-[10px] text-slate-400 mt-0.5">JPG · PNG · PDF · WEBP</div>
                  <input
                    id="sub-file-input" type="file" multiple accept="image/*,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length) setPendingAttachments((p) => [...p, ...files].slice(0, 20));
                      e.target.value = '';
                    }}
                    data-testid="vehicle-sub-file-input"
                  />
                </div>
                {pendingAttachments.length > 0 && (
                  <div className="grid grid-cols-4 gap-2" data-testid="vehicle-sub-pending-grid">
                    {pendingAttachments.map((f, i) => (
                      <div key={i} className="relative aspect-square rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                        {f.type?.startsWith('image/') ? (
                          <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex flex-col items-center p-1"><FileText className="h-5 w-5 text-slate-400" /><span className="text-[9px] mt-1 truncate w-full text-center px-1">{f.name}</span></div>
                        )}
                        <button type="button" onClick={() => setPendingAttachments((p) => p.filter((_, idx) => idx !== i))}
                          className="absolute top-1 right-1 h-5 w-5 rounded-full bg-rose-600 text-white flex items-center justify-center">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setSubModal((s) => ({ ...s, open: false }))}>İptal</Button>
              <Button
                size="sm"
                disabled={!subModal.loaner_plate.trim() || !subModal.start_date}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={async () => {
                  try {
                    const res = await api.post(`/vehicles/${vehicleId}/substitutions`, {
                      loaner_plate: subModal.loaner_plate.trim(),
                      loaner_brand: subModal.loaner_brand.trim() || null,
                      loaner_model: subModal.loaner_model.trim() || null,
                      loaner_source: subModal.loaner_source || null,
                      reason: subModal.reason.trim() || null,
                      start_date: subModal.start_date,
                      end_date: subModal.end_date || null,
                      notes: subModal.notes.trim() || null,
                    });
                    const newSubId = res.data?.data?.id;
                    // Şub 16, 2026 — Bekleyen ekleri upload et
                    if (newSubId && pendingAttachments.length) {
                      const fd = new FormData();
                      pendingAttachments.forEach((f) => fd.append('files', f, f.name));
                      try {
                        await api.post(`/vehicle-substitutions/${newSubId}/attachments`, fd, {
                          headers: { 'Content-Type': 'multipart/form-data' },
                        });
                      } catch (_e) {
                        toast.warning('İkame kaydedildi ama bazı ekler yüklenemedi');
                      }
                    }
                    toast.success('İkame araç eklendi');
                    setSubModal((s) => ({ ...s, open: false }));
                    setPendingAttachments([]);
                    fetchSubstitutions();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Kaydedilemedi');
                  }
                }}
                data-testid="vehicle-sub-save"
              >
                Kaydet
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ---- HELPER COMPONENTS ----

function EditableCard({ title, icon, isEditing, onEdit, onSave, onCancel, saving, highlight, t, children }) {
  return (
    <Card className={highlight ? 'border-amber-300' : ''}>
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
        {isEditing ? (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onCancel} className="h-7 text-xs" data-testid={`cancel-edit-${title}`}>
              <X className="h-3 w-3 mr-1" />{t?.('common.cancel') || 'Cancel'}
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving} className="h-7 text-xs" data-testid={`save-edit-${title}`}>
              {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}{t?.('common.save') || 'Save'}
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="sm" onClick={onEdit} className="h-7 text-xs text-blue-600 hover:text-blue-700" data-testid={`edit-btn-${title}`}>
            <Pencil className="h-3 w-3 mr-1" />{t?.('common.edit') || 'Edit'}
          </Button>
        )}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function EditField({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="h-8 text-sm" />
    </div>
  );
}

function EditSelect({ label, value, onChange, options, labels, t }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-sm"><SelectValue placeholder={t?.('common.select') || 'Select...'} /></SelectTrigger>
        <SelectContent>
          {options.map(opt => <SelectItem key={opt} value={opt}>{labels?.[opt] || opt}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function InfoRow({ label, value, mono, badge, highlight, warn }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-slate-700 last:border-0">
      <span className="text-slate-500 dark:text-slate-400 text-xs">{label}</span>
      {value ? (
        badge ? (
          <Badge variant="outline" className="text-xs">{value}</Badge>
        ) : (
          <span className={`font-medium text-sm ${mono ? 'font-mono' : ''} ${highlight ? 'text-red-600 font-semibold' : warn ? 'text-amber-600' : 'text-slate-800 dark:text-slate-200'}`}>
            {value}
          </span>
        )
      ) : (
        <span className="text-xs text-slate-300">-</span>
      )}
    </div>
  );
}

// Şub 16, 2026 — İkame ek thumbnail (Bearer korumalı blob URL)
function SubAttachThumb({ att, onDelete }) {
  const [blobUrl, setBlobUrl] = React.useState(null);
  const isImg = (att.content_type || '').startsWith('image/');
  React.useEffect(() => {
    let alive = true; let currentUrl = null;
    if (isImg) {
      (async () => {
        try {
          const res = await api.get(`/vehicle-substitution-attachments/${att.id}/download?preview=1`, { responseType: 'blob' });
          if (!alive) return;
          currentUrl = URL.createObjectURL(res.data);
          setBlobUrl(currentUrl);
        } catch { /* ignore */ }
      })();
    }
    return () => { alive = false; if (currentUrl) { try { URL.revokeObjectURL(currentUrl); } catch { /* ignore */ } } };
  }, [att.id, isImg]);
  const open = async () => {
    try {
      const res = await api.get(`/vehicle-substitution-attachments/${att.id}/download?preview=1`, { responseType: 'blob' });
      const u = URL.createObjectURL(res.data);
      window.open(u, '_blank', 'noopener');
      setTimeout(() => { try { URL.revokeObjectURL(u); } catch { /* ignore */ } }, 60000);
    } catch { /* ignore */ }
  };
  return (
    <div className="relative group aspect-square rounded border border-slate-200 bg-slate-50 overflow-hidden">
      <button type="button" onClick={open} className="absolute inset-0 flex items-center justify-center" title={att.file_name}>
        {isImg && blobUrl
          ? <img src={blobUrl} alt={att.file_name} className="w-full h-full object-cover" />
          : <FileText className="h-4 w-4 text-slate-400" />}
      </button>
      <button
        type="button" onClick={onDelete}
        className="absolute top-0.5 right-0.5 h-4 w-4 rounded-full bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
        title="Sil"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}

